import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, Sun, Moon } from 'lucide-react';
import { PHONES, CONTACT } from '../content';
import { useTheme } from '../lib/ThemeContext';

const NAV = [
  { l: 'Anasayfa',        to: '/' },
  { l: 'Acil Yol Yardım', to: '/acil-yol-yardim' },
  { l: 'Hizmetlerimiz',   to: '/hizmetlerimiz' },
  { l: 'Garanti',         to: '/garanti' },
  { l: 'Blog',            to: '/blog' },
  { l: 'İletişim',        to: '/iletisim' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);
  const { pathname }            = useLocation();
  const { theme, toggle }       = useTheme();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const isActive = (to) => pathname === to;

  return (
    <>
      {/* ─── Emergency Bar ─── */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 60,
        background: theme === 'dark' ? 'rgba(229,51,51,.08)' : 'rgba(229,51,51,.06)',
        borderBottom: theme === 'dark' ? '1px solid rgba(229,51,51,.15)' : '1px solid rgba(229,51,51,.12)',
        backdropFilter: 'blur(12px)',
        padding: '.35rem 0',
      }}>
        <div className="wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} className="live" />
            <span style={{ fontSize: '.7rem', color: 'var(--green)', fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase' }}>
              Şu An Aktif — 7/24 Kocaeli'ye Hizmet Veriyoruz
            </span>
          </div>
        </div>
      </div>

      {/* ─── Main Nav ─── */}
      <header
        data-testid="navbar"
        style={{
          position: 'fixed', top: 26, left: 0, right: 0, zIndex: 50,
          background: scrolled
            ? (theme === 'dark' ? 'rgba(5,5,13,.92)' : 'rgba(255,255,255,.92)')
            : (theme === 'dark' ? 'rgba(5,5,13,.6)' : 'rgba(255,255,255,.6)'),
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${scrolled ? 'var(--border)' : 'transparent'}`,
          transition: 'background .3s, border-color .3s',
        }}
      >
        <div className="wrap nav-bar-height" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 76 }}>

          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '.75rem', flexShrink: 0 }}>
            <div className="nav-logo-circle" style={{
              width: 54, height: 54, borderRadius: '50%', background: 'var(--red)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
              fontSize: '1rem', color: '#fff', letterSpacing: '-.01em',
            }}>7/24</div>
            <div>
              <div className="nav-logo-text" style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '1.65rem', color: 'var(--text)', lineHeight: 1.1 }}>7/24 Lastik</div>
              <div className="nav-logo-sub" style={{ fontSize: '.85rem', color: 'var(--muted)', letterSpacing: '.02em' }}>Kocaeli Acil Yol Yardım</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="nav-desktop" style={{ display: 'flex', gap: '.125rem' }}>
            {NAV.map(({ l, to }) => (
              <Link key={to} to={to}
                style={{
                  padding: '.4rem .8rem', borderRadius: 8,
                  fontSize: '.845rem', fontWeight: 500,
                  color: isActive(to) ? 'var(--blue)' : 'var(--text2)',
                  transition: 'all .18s',
                }}
                onMouseEnter={e => { if (!isActive(to)) { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.background = 'var(--surface)'; } }}
                onMouseLeave={e => { if (!isActive(to)) { e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.background = 'transparent'; } }}
              >{l}</Link>
            ))}
          </nav>

          {/* Right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '.625rem', flexShrink: 0 }}>
            {/* Theme toggle */}
            <button
              data-testid="theme-toggle"
              onClick={toggle}
              aria-label={theme === 'dark' ? 'Gündüz modu' : 'Gece modu'}
              className="nav-theme-toggle"
              style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'var(--card)', border: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: theme === 'dark' ? '#facc15' : 'var(--blue)',
                cursor: 'pointer', transition: 'all .2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.background = 'var(--card2)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--card)'; }}
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <a href={PHONES.main_tel}
              data-testid="header-call-button"
              className="btn btn-primary btn-sm num nav-header-call"
              style={{ gap: '.375rem', letterSpacing: '.01em', padding: '.5rem 1rem', fontSize: '.825rem' }}>
              <Phone size={13} />
              {PHONES.main}
            </a>
            <button
              data-testid="mobile-menu-open-button"
              className="nav-burger"
              onClick={() => setOpen(true)}
              style={{ display: 'none', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 9, padding: '.45rem', color: 'var(--text)' }}>
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'var(--bg)', display: 'flex', flexDirection: 'column', padding: '1.25rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, fontSize: '.62rem', color: '#fff' }}>7/24</div>
              <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, color: 'var(--text)' }}>7/24 Lastik</span>
            </Link>
            <button data-testid="mobile-menu-close-button" onClick={() => setOpen(false)}
              style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 9, padding: '.45rem', color: 'var(--text)' }}>
              <X size={18} />
            </button>
          </div>
          <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
            {NAV.map(({ l, to }) => (
              <Link key={to} to={to} style={{
                padding: '.85rem 1.125rem', borderRadius: 11, fontSize: '1rem', fontWeight: 500,
                color: isActive(to) ? 'var(--blue)' : 'var(--text)',
                background: isActive(to) ? 'var(--surface)' : 'transparent',
                border: `1px solid ${isActive(to) ? 'var(--border)' : 'transparent'}`,
              }}>{l}</Link>
            ))}
          </nav>
          <div style={{ paddingTop: '1.5rem', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '.625rem' }}>
            <a href={PHONES.main_tel} className="btn btn-primary btn-lg num ring" style={{ justifyContent: 'center' }}>
              <Phone size={16} /> {PHONES.main}
            </a>
            <a href={PHONES.secondary_tel} className="btn btn-secondary num" style={{ justifyContent: 'center' }}>
              <Phone size={14} /> {PHONES.secondary}
            </a>
          </div>
        </div>
      )}

      {/* Mobile bottom bar */}
      <div className="mob-cta" style={{
        display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 90,
        background: 'var(--bg2)', borderTop: '1px solid var(--border)',
        padding: '.875rem 1rem', gap: '.75rem',
        boxShadow: '0 -8px 32px rgba(0,0,0,.5)',
      }}>
        <a href={PHONES.main_tel} data-testid="mobile-call-bar"
          className="btn btn-primary ring num"
          style={{ flex: 1, justifyContent: 'center', gap: '.5rem', fontSize: '.9rem' }}>
          <Phone size={16} /> {PHONES.main}
        </a>
        <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
          data-gtm-action="whatsapp_click"
          className="btn btn-green"
          style={{ flexShrink: 0, gap: '.5rem', fontSize: '.9rem', padding: '.875rem 1.25rem' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
            <path d="M5.077 18.828l.532-1.869A8.85 8.85 0 0 1 3.2 12C3.2 7.134 7.134 3.2 12 3.2c4.866 0 8.8 3.934 8.8 8.8 0 4.866-3.934 8.8-8.8 8.8a8.845 8.845 0 0 1-4.893-1.471l-2.03.499zM12 1C6.477 1 2 5.477 2 11c0 1.821.439 3.538 1.215 5.057L2 22l6.071-1.59A10.976 10.976 0 0 0 12 21c5.523 0 10-4.477 10-10S17.523 1 12 1z"/>
          </svg>
        </a>
      </div>
    </>
  );
}
